
/**
 * Simple rate limiter to prevent too many API calls
 */
class RateLimiter {
  private limits: Map<string, { timestamp: number; count: number }> = new Map();
  
  // Default rate limiting parameters
  private defaultMaxRequests = 5;
  private defaultTimeWindow = 10000; // 10 seconds in milliseconds
  
  /**
   * Check if a particular key is rate limited
   * @param key The key to check rate limiting for
   * @param maxRequests Optional: Maximum requests allowed in the time window
   * @param timeWindow Optional: Time window in milliseconds
   * @returns boolean - true if the request can proceed (not rate limited)
   */
  checkRateLimit(
    key: string, 
    maxRequests: number = this.defaultMaxRequests,
    timeWindow: number = this.defaultTimeWindow
  ): boolean {
    const now = Date.now();
    const limit = this.limits.get(key);
    
    // If no previous requests or outside the time window, reset counter
    if (!limit || now - limit.timestamp > timeWindow) {
      this.limits.set(key, { timestamp: now, count: 1 });
      return true;
    }
    
    // If within the time window but under the limit, increment counter
    if (limit.count < maxRequests) {
      this.limits.set(key, { timestamp: limit.timestamp, count: limit.count + 1 });
      return true;
    }
    
    // Rate limited - request cannot proceed
    return false;
  }
  
  /**
   * Reset rate limiting for a specific key
   * @param key The key to reset
   */
  resetLimit(key: string): void {
    this.limits.delete(key);
  }
  
  /**
   * Clear all rate limiting data
   */
  clearAllLimits(): void {
    this.limits.clear();
  }

  /**
   * Execute a function with rate limiting
   * @param key Identifier for the rate limiting
   * @param fn Function to execute if not rate-limited
   * @param options Optional configuration
   * @returns Promise with the result of the function
   */
  async limit<T>(
    key: string,
    fn: () => Promise<T>,
    options: { fallbackValue?: T; throwOnLimit?: boolean } = {}
  ): Promise<T> {
    if (this.checkRateLimit(key)) {
      return fn();
    }

    if (options.throwOnLimit) {
      throw new Error(`Rate limit exceeded for ${key}`);
    }

    if ('fallbackValue' in options) {
      return options.fallbackValue as T;
    }

    throw new Error(`Rate limit exceeded for ${key}`);
  }

  /**
   * Get recommended backoff delay based on attempt number
   * @param attempt Current attempt number (0-based)
   * @returns Delay in milliseconds
   */
  getBackoffDelay(attempt: number): number {
    // Exponential backoff with jitter: 2^attempt * 100ms + random jitter
    const baseDelay = Math.min(2 ** attempt * 100, 10000); // Max 10s delay
    const jitter = Math.random() * 100;
    return baseDelay + jitter;
  }
}

export const rateLimiter = new RateLimiter();
